import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { theme } from '_constants/theme';
import { selectCreating, selectData, selectForm, selectLoading } from '_store/amendment/selector';
import { createAmendment, getAllTaxReturns, refreshForm } from '_store/amendment/slice';
import {
  selectActiveBusinessTaxFilingProductId,
  selectActiveOrderId,
  selectActivePersonalTaxFilingProductId,
} from '_store/order/selector';
import { selectCustomerId } from '_store/user/selector';

import Card from 'components/atomic/layouts/Card';
import Text from 'components/atomic/layouts/Text';
import AmendmentRequestForm from 'components/composed/forms/AmendmentRequestForm';

const StyledCard = styled(Card)`
  position: relative;
`;

const StyledBackground = styled.div`
  box-sizing: border-box;

  position: absolute;
  left: 0;
  right: 0;
  top: -2px;
  bottom: 0;

  background: radial-gradient(100% 100% at 50.14% 0%, rgba(196, 175, 255, 0) 86.98%, rgba(196, 175, 255, 0.64) 100%),
    radial-gradient(100% 100% at 50.14% 0%, rgba(196, 175, 255, 0) 49.48%, rgba(196, 175, 255, 0.32) 100%), #ffffff;
  border-radius: 24px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
`;

const AmendmentRequestView = () => {
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const orderId = useSelector(selectActiveOrderId);
  const activeBusinessTaxFilingProductId = useSelector(selectActiveBusinessTaxFilingProductId);
  const activePersonalTaxFilingProductId = useSelector(selectActivePersonalTaxFilingProductId);
  const data = useSelector(selectData);
  const creating = useSelector(selectCreating);
  const loading = useSelector(selectLoading);
  const form = useSelector(selectForm);

  const handleSubmit = (values) => {
    if (orderId && values) {
      dispatch(refreshForm({ ...values }));
      dispatch(createAmendment({ orderId }));
    }
  };

  const taxReturnOptions = useMemo(() => {
    return data.map(({ productId, id, year, description }) => ({
      title: `${year} ${activeBusinessTaxFilingProductId === productId ? 'Business' : 'Personal'} - ${description}`,
      value: id,
    }));
  }, [data, activeBusinessTaxFilingProductId]);

  useEffect(() => {
    if (customerId && activeBusinessTaxFilingProductId && activePersonalTaxFilingProductId && !data.length) {
      dispatch(
        getAllTaxReturns({
          customerId,
          productIds: [activeBusinessTaxFilingProductId, activePersonalTaxFilingProductId],
        })
      );
    }
  }, [customerId, activeBusinessTaxFilingProductId, activePersonalTaxFilingProductId, data]);

  return (
    <StyledCard>
      <StyledHeader>
        <StyledTitleContainer>
          <Text size={theme.fontSizes.medium} weight={600} bottomSpace={theme.spaces.large6X}>
            Amendment?
          </Text>
        </StyledTitleContainer>
      </StyledHeader>
      <AmendmentRequestForm
        taxReturnOptions={taxReturnOptions}
        loading={creating}
        submitDisabled={creating || loading}
        defaultValues={form}
        onSubmit={handleSubmit}
      />
      <StyledBackground />
    </StyledCard>
  );
};

export default AmendmentRequestView;
